import React, { useEffect, useState } from 'react';

import { API_QUOTA_QUEUED_EVENT } from '../services/axiosInstance';
import type { QuotaQueueInfo } from '../utils/quotaQueue';

import { QuotaQueueNotice } from './QuotaQueueNotice';

/** Requests whose page shows the queue notice itself, with the button that sends the request again. */
const HANDLED_INLINE = [
  /\/proposal\/export$/,
  /\/tasks\/ai-generate$/,
  /\/api\/v1\/loops(\/[^/]+(\/messages|\/run)?)?$/,
  /\/proposal\/videos\/[^/]+\/retry$/,
  /\/promotion\/shares\/[^/]+\/retry$/,
];

interface QueuedRequest {
  info: QuotaQueueInfo;
  url: string;
}

export const handledInline = (url: string): boolean => HANDLED_INLINE.some((pattern) => pattern.test(url.split('?')[0]));

const AFTER_REDIRECT_KEY = 'quota_notice_after_redirect';

/** Show the plan notice for a refusal met outside the API client (a fetch stream). */
export const showQuotaNotice = (info: QuotaQueueInfo): void => {
  window.dispatchEvent(new globalThis.CustomEvent(API_QUOTA_QUEUED_EVENT, { detail: { info, url: '' } }));
};

/** Show this notice on the next page, for a refusal met just before a full-page redirect (sign-in callbacks). */
export const rememberQuotaNotice = (info: QuotaQueueInfo): void => {
  try {
    window.sessionStorage.setItem(AFTER_REDIRECT_KEY, JSON.stringify(info));
  } catch { /* storage unavailable: the page simply shows no notice */ }
};

const takeRememberedNotice = (): QueuedRequest | null => {
  try {
    const raw = window.sessionStorage.getItem(AFTER_REDIRECT_KEY);

    window.sessionStorage.removeItem(AFTER_REDIRECT_KEY);

    return raw ? { info: JSON.parse(raw) as QuotaQueueInfo, url: '' } : null;
  } catch {
    return null;
  }
};

/** The queue notice for any request the API answered "queued for your plan's hourly quota", wherever it was made. */
export const GlobalQuotaNotice: React.FC = () => {
  const [queued, setQueued] = useState<QueuedRequest | null>(takeRememberedNotice);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as globalThis.CustomEvent<QueuedRequest>).detail;

      if (detail?.info && !handledInline(detail.url || '')) setQueued(detail);
    };

    window.addEventListener(API_QUOTA_QUEUED_EVENT, handler);

    return () => window.removeEventListener(API_QUOTA_QUEUED_EVENT, handler);
  }, []);

  if (!queued) return null;

  return (
    <div className="global-quota-notice" role="region" aria-label="Plan quota">
      <QuotaQueueNotice source="global:quota-notice" notice={queued.info} />
      <button type="button" className="global-quota-notice__close" aria-label="Dismiss"
        onClick={() => setQueued(null)}>×</button>
    </div>
  );
};
